import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import uniqBy from 'lodash/uniqBy'

import api from '@disco/api/server'
import { serializeAggregatedTrack } from '@disco/api/utils/aggregated-search-track'
import { TRACKS_UPDATE } from '@disco/constants/ActionTypes'
import { NESTED_TRACKS_IN_SEARCH } from '@disco/constants/FeatureTypes'
import { fetchNestedTracks } from '@disco/state-management/actions/PlaylistTrackActions'
import { getConstanceSetting } from '@disco/state-management/selectors/user'
import { normalizeV2Track } from '@disco/utils/search-page'
import { getNestedTracksCount } from '@disco/utils/tracks'

import type { Group } from 'types/group'
import type { State } from 'types/redux'
import type { V2SearchQuery } from 'types/search-page'
import type { Track, TrackId } from 'types/track'

type SearchThunk<Result> = ThunkAction<Result, State, unknown, UnknownAction>
type SuggestedTracksResult = {
  tracks: Track[]
  nextCursor: string | null
}

// Bounds the round trips spent skipping pages whose hits are all filtered out.
const MAX_SUGGESTION_PAGES = 3

/** Normalizes V2 track hits and hydrates their shared track and nesting state. */
export const hydrateV2SearchTracks =
  (hits: Array<Track | Group>): SearchThunk<Track[]> =>
  (dispatch, getState) => {
    const tracks = hits
      .filter((hit): hit is Track => 'type' in hit && hit.type === 'track')
      .map(hit => {
        const track = normalizeV2Track(hit) as Track
        return track.source ? serializeAggregatedTrack(track) : track
      })

    if (tracks.length === 0) return tracks
    dispatch({ type: TRACKS_UPDATE, tracks })

    if (getConstanceSetting(getState(), NESTED_TRACKS_IN_SEARCH)) {
      const mainTrackIds = tracks
        .filter(track => getNestedTracksCount(track) > 0)
        .map(track => track.id)
      if (mainTrackIds.length > 0) {
        dispatch(
          fetchNestedTracks(mainTrackIds, { includePlaylistsCount: true }),
        )
      }
    }

    return tracks
  }

const assertNotAborted = (signal: AbortSignal) => {
  if (signal.aborted) {
    const error = new Error('Search aborted')
    error.name = 'AbortError'
    throw error
  }
}

/**
 * Fetches similar tracks, dropping duplicates, excluded tracks and tracks
 * outside the user's territories. Pages past results that are all filtered out.
 */
export const fetchSuggestedTracks =
  (
    query: V2SearchQuery,
    signal: AbortSignal,
    excludedTrackIds: readonly TrackId[] = [],
  ): SearchThunk<Promise<SuggestedTracksResult>> =>
  async dispatch => {
    const excludedIds = new Set(excludedTrackIds)
    const visitedCursors = new Set(query.cursor ? [query.cursor] : [])
    let pageQuery = query

    for (let page = 1; ; page += 1) {
      assertNotAborted(signal)
      const response = await api.searchV2(pageQuery, signal)
      assertNotAborted(signal)

      const tracks = uniqBy(
        response.hits
          .filter((hit): hit is Track => hit.type === 'track')
          .filter(
            track =>
              !track.outside_your_territories && !excludedIds.has(track.id),
          ),
        'id',
      )
      const { cursor, has_more } = response.pagination
      // A repeated cursor would replay a page we have already shown.
      const nextCursor =
        has_more && cursor && !visitedCursors.has(cursor) ? cursor : null

      if (tracks.length > 0 || !nextCursor || page >= MAX_SUGGESTION_PAGES) {
        return { tracks: dispatch(hydrateV2SearchTracks(tracks)), nextCursor }
      }

      visitedCursors.add(nextCursor)
      pageQuery = { ...query, cursor: nextCursor }
    }
  }
